import type { KpiDashboardConfig } from './kpiConfig';

// All functions are pure — no side effects, no imports from React.
// Dates in processed items (after dataProcessor) are DD/MM/YYYY strings.
// Raw item.Date (before dataProcessor) is a JS Date object from the API.

function safeArray(arr: any): any[] {
  return Array.isArray(arr) ? arr : [];
}

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Converts "MM-YYYY" → "YYYY-MM" so alphabetical sort equals chronological sort.
function periodSortKey(period: string): string {
  const parts = period.split('-');
  return parts.length === 2 ? `${parts[1]}-${parts[0].padStart(2, '0')}` : period;
}

// GetPeriod produces "MM-YYYY" (e.g. "05-2025"). Convert to "Mon 'YY" (e.g. "May '25").
// Also handles legacy "MMM-YYYY" format where parseInt returns NaN.
function periodLabel(period: string): string {
  if (!period || typeof period !== 'string') return '';
  const parts = period.split('-');
  const first = parts[0] ?? period;
  const mm = parseInt(first, 10);
  if (!isNaN(mm) && mm >= 1 && mm <= 12) {
    const yearSuffix = parts[1] ? ` '${parts[1].slice(-2)}` : '';
    return `${MONTH_SHORT[mm - 1]}${yearSuffix}`;
  }
  return first; // already a name (e.g. "Jan") — return as-is
}

function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const p = s.split('/');
  if (p.length !== 3) return null;
  const d = parseInt(p[0], 10), m = parseInt(p[1], 10) - 1, y = parseInt(p[2], 10);
  if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
  return new Date(y, m, d);
}

// ─── Module 1: Compliance ───────────────────────────────────────────────────

export function calcAvgResolutionDays(observations: any[], sameAndNextDayAsOne = true): number {
  const closed = safeArray(observations).filter(i => i.Status === 'Closed' && i.DateClosedOut && i.Date);
  if (!closed.length) return 0;
  const total = closed.reduce((sum, i) => {
    const created = parseDate(i.Date);
    const closedDate = parseDate(i.DateClosedOut);
    if (!created || !closedDate) return sum;
    const raw = (closedDate.getTime() - created.getTime()) / 86400000;
    // sameAndNextDayAsOne: 0 days → 1, 1 day → 1, 2+ days → as-is
    return sum + (sameAndNextDayAsOne ? Math.max(1, Math.round(raw)) : Math.max(0, raw));
  }, 0);
  return Math.round((total / closed.length) * 10) / 10;
}

export function calcOverduePercent(observations: any[]): number {
  const observations_ = safeArray(observations);
  if (!observations_.length) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = observations_.filter(i => {
    if (i.Status === 'Closed') return false;
    const due = parseDate(i.DueDate);
    return due ? due < today : false;
  });
  return Math.round((overdue.length / observations_.length) * 100);
}

export function calcOverdueCount(observations: any[]): { overdueCount: number; openCount: number; fillPct: number } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const open = safeArray(observations).filter(i => i.Status !== 'Closed');
  const overdue = open.filter(i => {
    const due = parseDate(i.DueDate);
    return due ? due < today : false;
  });
  const fillPct = open.length > 0 ? Math.round((overdue.length / open.length) * 100) : 0;
  return { overdueCount: overdue.length, openCount: open.length, fillPct };
}

export function calcTopCategories(observations: any[], topN = 5): { label: string; value: number }[] {
  const counts: Record<string, number> = {};
  safeArray(observations).forEach(i => {
    const cat = ((i.Category as string) || 'Uncategorised').trim();
    counts[cat] = (counts[cat] || 0) + 1;
  });
  return Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, topN)
    .map(([label, value]) => ({ label, value }));
}

// Period-over-period change: positive changePct = fewer days = improvement
export function calcResolutionTrend(
  observations: any[],
  sameAndNextDayAsOne = true,
): { changePct: number; prevPeriodLabel: string } | null {
  const byPeriod: Record<string, any[]> = {};
  safeArray(observations)
    .filter(i => i.Status === 'Closed' && i.Period)
    .forEach(i => {
      if (!byPeriod[i.Period]) byPeriod[i.Period] = [];
      byPeriod[i.Period].push(i);
    });
  const periods = Object.keys(byPeriod).sort((a, b) => periodSortKey(a).localeCompare(periodSortKey(b)));
  if (periods.length < 2) return null;
  const prevKey = periods[periods.length - 2];
  const currKey = periods[periods.length - 1];
  const prevAvg = calcAvgResolutionDays(byPeriod[prevKey], sameAndNextDayAsOne);
  const currAvg = calcAvgResolutionDays(byPeriod[currKey], sameAndNextDayAsOne);
  if (!prevAvg) return null;
  return {
    changePct: Math.round(((prevAvg - currAvg) / prevAvg) * 100),
    prevPeriodLabel: periodLabel(prevKey),
  };
}

// Resolution time grouped by Period for the trend bar chart
export function calcResolutionByPeriod(
  observations: any[],
  sameAndNextDayAsOne = true,
): { label: string; value: number }[] {
  const byPeriod: Record<string, any[]> = {};
  safeArray(observations)
    .filter(i => i.Status === 'Closed' && i.Period)
    .forEach(i => {
      if (!byPeriod[i.Period]) byPeriod[i.Period] = [];
      byPeriod[i.Period].push(i);
    });
  return Object.entries(byPeriod)
    .sort(([a], [b]) => periodSortKey(a).localeCompare(periodSortKey(b)))
    .slice(-6)
    .map(([period, items]) => ({
      label: periodLabel(period),
      value: Math.round(calcAvgResolutionDays(items, sameAndNextDayAsOne) * 10) / 10,
    }));
}

// ─── Module 2: Sustainability ────────────────────────────────────────────────

export function calcGHGEmissions(energy: any[], waste: any[], config: KpiDashboardConfig): number {
  let totalKgCO2e = 0;
  safeArray(energy).forEach(i => {
    const qty = parseFloat(i.Quantitym3) || 0;
    const fuel = (i.FuelTypeEnergySource || '').trim();
    if (/electric/i.test(fuel)) {
      totalKgCO2e += qty * config.emissionFactors.electricityGrid;
    } else {
      totalKgCO2e += qty * (config.emissionFactors.fuels[fuel] || 0);
    }
  });
  safeArray(waste).forEach(i => {
    if (i.Diverted === 'Not Diverted') {
      totalKgCO2e += (parseFloat(i.QuantityTonne) || 0) * 1000 * config.emissionFactors.landfill;
    }
  });
  return Math.round(totalKgCO2e / 1000); // kg → tonnes CO2e
}

function isDiverted(item: any): boolean | null {
  if (item.Diverted === 'Diverted') return true;
  if (item.Diverted === 'Not Diverted') return false;
  // Fallback: checkDiverted may not have matched; check WasteDisposalMethod directly
  const method = (item.WasteDisposalMethod || '').trim().toLowerCase();
  if (!method) return null;
  if (/reuse|recycl|recover|compost|energy\s*recover/i.test(method)) return true;
  if (/landfill|dump|incinerat|other/i.test(method)) return false;
  return null;
}

export function calcWasteDiversionRate(waste: any[]): number {
  const waste_ = safeArray(waste);
  const total = waste_.reduce((s, i) => s + (parseFloat(i.QuantityTonne) || 0), 0);
  if (!total) return 0;
  const diverted = waste_
    .filter(i => isDiverted(i) === true)
    .reduce((s, i) => s + (parseFloat(i.QuantityTonne) || 0), 0);
  return Math.round((diverted / total) * 100);
}

// Sums per-package values for each selected package, falling back to `default` for any
// package that isn't listed in byPackage. No selection (null/empty) uses a single `default`.
function sumByPackage(pkgs: string[] | null, source: { byPackage: Record<string, number>; default: number }): number {
  if (!pkgs || pkgs.length === 0) return source.default;
  return pkgs.reduce((sum, p) => sum + (source.byPackage[p] ?? source.default), 0);
}

export function calcResourceIntensity(
  water: any[],
  energy: any[],
  pkg: string[] | null,
  config: KpiDashboardConfig,
): { waterPerManHour: number; kwhPerSqm: number } {
  const workforce = sumByPackage(pkg, config.workforce);
  const area = sumByPackage(pkg, config.projectArea);
  const totalWaterL = safeArray(water).reduce((s, i) => s + (parseFloat(i.Quantitym3) || parseFloat(i.QuantityTonne) || 0), 0) * 1000;
  const waterPeriods = new Set(safeArray(water).map(i => i.Period).filter(Boolean));
  const monthCount   = Math.max(1, waterPeriods.size);
  const manHours     = workforce * config.workingDaysPerMonth * config.workingHoursPerDay * monthCount;
  const waterPerManHour = manHours > 0 ? Math.round((totalWaterL / manHours) * 10) / 10 : 0;
  // Convert each energy entry to kWh: electricity Quantitym3 is already kWh (factor=1),
  // all other fuels use their calorific value (kWh/m³) from config.
  const totalKwh = safeArray(energy).reduce((s, i) => {
    const qty  = parseFloat(i.Quantitym3) || 0;
    const fuel = (i.FuelTypeEnergySource || '').trim();
    const factor = /electric/i.test(fuel) ? 1 : (config.calorificValues[fuel] ?? 0);
    return s + qty * factor;
  }, 0);
  const kwhPerSqm = area > 0 ? Math.round((totalKwh / area) * 10) / 10 : 0;
  return { waterPerManHour, kwhPerSqm };
}

export function calcWasteByPeriod(waste: any[]): { label: string; diverted: number; notDiverted: number }[] {
  const byPeriod: Record<string, { diverted: number; notDiverted: number }> = {};
  safeArray(waste).filter(i => i.Period).forEach(i => {
    if (!byPeriod[i.Period]) byPeriod[i.Period] = { diverted: 0, notDiverted: 0 };
    const qty = parseFloat(i.QuantityTonne) || 0;
    if (i.Diverted === 'Diverted') byPeriod[i.Period].diverted += qty;
    else byPeriod[i.Period].notDiverted += qty;
  });
  return Object.entries(byPeriod)
    .sort(([a], [b]) => periodSortKey(a).localeCompare(periodSortKey(b)))
    .slice(-6)
    .map(([period, v]) => ({ label: periodLabel(period), ...v }));
}

// ─── Module 3: Material Circularity ─────────────────────────────────────────

function isSpoilReused(item: any): boolean | null {
  if (item.Reused === 'Reuse') return true;
  if (item.Reused === 'Not Reused') return false;
  // Fallback: Reused field may be null if processData_Spoil didn't match — check raw field directly
  const method = (item.SpoilDisposalMethod || '').trim().toLowerCase();
  if (!method) return null;
  if (/reuse|re-use|temporary storage/i.test(method)) return true;
  if (/landfill|disposal off-site|slurry|other/i.test(method)) return false;
  return null;
}

export function calcNetSpoilBalance(
  spoil: any[],
  importedSoil: any[],
): { generated: number; reused: number; disposed: number; imported: number; net: number; unit: 'm³' | 't' } {
  const all = [...safeArray(spoil), ...safeArray(importedSoil)];
  // Use whichever field is actually populated; QuantityTonne takes precedence if present.
  const hasTonnes = all.some(i => parseFloat(i.QuantityTonne) > 0);
  const qty = (i: any) => hasTonnes
    ? parseFloat(i.QuantityTonne) || 0
    : parseFloat(i.Quantitym3) || parseFloat(i.QuantityTonne) || 0;
  const spoil_ = safeArray(spoil);
  const generated = Math.round(spoil_.reduce((s, i) => s + qty(i), 0));
  const reused    = Math.round(spoil_.filter(i => isSpoilReused(i) === true).reduce((s, i) => s + qty(i), 0));
  const disposed  = Math.round(spoil_.filter(i => isSpoilReused(i) === false).reduce((s, i) => s + qty(i), 0));
  const imported  = Math.round(safeArray(importedSoil).reduce((s, i) => s + qty(i), 0));
  return { generated, reused, disposed, imported, net: generated - imported, unit: hasTonnes ? 't' : 'm³' };
}

export function calcRegionalRatio(regional: any[], recycled: any[]): number {
  const regionalVal = safeArray(regional).reduce((s, i) => s + (parseFloat(i.MaterialValue) || 0), 0);
  const recycledVal = safeArray(recycled).reduce((s, i) => s + (parseFloat(i.MaterialValue) || 0), 0);
  const total = regionalVal + recycledVal;
  return total > 0 ? Math.round((regionalVal / total) * 100) : 0;
}

export function calcRecycledContentPercent(recycled: any[]): number {
  const recycled_ = safeArray(recycled);
  const totalMaterial = recycled_.reduce((s, i) => s + (parseFloat(i.MaterialValue) || 0), 0);
  const totalRecycled = recycled_.reduce(
    (s, i) => s + (parseFloat(i.TotalValueOfRecycleContentMaterial) || 0),
    0,
  );
  return totalMaterial > 0 ? Math.round((totalRecycled / totalMaterial) * 100) : 0;
}

// ─── Module 4: Community Nuisance ───────────────────────────────────────────

export interface BreachSummary {
  totalBreachDays: number;
  totalMonitoringDays: number;
  breachPercent: number;
}

export function calcBreachRate(
  noise: any[],
  vibration: any[],
  pm10: any[],
  pm25: any[],
): BreachSummary {
  const monitoringDays = new Set<string>();
  const breachDays = new Set<string>();
  [...safeArray(noise), ...safeArray(vibration), ...safeArray(pm10), ...safeArray(pm25)].forEach(i => {
    if (!i.Date) return;
    monitoringDays.add(i.Date);
    if (i.Conformity === 'Non Conformity') breachDays.add(i.Date);
  });
  return {
    totalBreachDays: breachDays.size,
    totalMonitoringDays: monitoringDays.size,
    breachPercent: monitoringDays.size > 0 ? Math.round((breachDays.size / monitoringDays.size) * 100) : 0,
  };
}

export interface CalendarDay {
  date: string;   // DD/MM/YYYY
  hasBreach: boolean;
}

export function calcNuisanceFreeDays(
  noise: any[],
  vibration: any[],
  pm10: any[],
  pm25: any[],
): { percent: number; totalBreachDays: number; calendarData: CalendarDay[] } {
  const breachDates = new Set<string>();
  const allDates = new Set<string>();
  [...safeArray(noise), ...safeArray(vibration), ...safeArray(pm10), ...safeArray(pm25)].forEach(i => {
    if (!i.Date) return;
    allDates.add(i.Date);
    if (i.Conformity === 'Non Conformity') breachDates.add(i.Date);
  });
  const total = allDates.size;
  return {
    percent: total > 0 ? Math.round(((total - breachDates.size) / total) * 100) : 100,
    totalBreachDays: breachDates.size,
    calendarData: Array.from(allDates).map(d => ({ date: d, hasBreach: breachDates.has(d) })),
  };
}

export function calcBreachByPeriod(
  noise: any[],
  vibration: any[],
  pm10: any[],
  pm25: any[],
): { label: string; noise: number; vibration: number; pm10: number; pm25: number }[] {
  const byPeriod: Record<string, { noise: number; vibration: number; pm10: number; pm25: number }> = {};
  const add = (arr: any[], key: 'noise' | 'vibration' | 'pm10' | 'pm25') => {
    safeArray(arr).filter(i => i.Period && i.Conformity === 'Non Conformity').forEach(i => {
      if (!byPeriod[i.Period]) byPeriod[i.Period] = { noise: 0, vibration: 0, pm10: 0, pm25: 0 };
      byPeriod[i.Period][key]++;
    });
  };
  add(noise, 'noise'); add(vibration, 'vibration'); add(pm10, 'pm10'); add(pm25, 'pm25');
  return Object.entries(byPeriod)
    .sort(([a], [b]) => periodSortKey(a).localeCompare(periodSortKey(b)))
    .slice(-6)
    .map(([period, v]) => ({ label: periodLabel(period), ...v }));
}

// ─── Module 5: Workforce ─────────────────────────────────────────────────────

export function calcTrainingCoverage(
  training: any[],
  pkg: string[] | null,
  config: KpiDashboardConfig,
): number {
  const workforce = sumByPackage(pkg, config.workforce);
  const total = safeArray(training).reduce((s, i) => s + (parseFloat(i.NoOfAttendees) || 0), 0);
  return Math.min(100, Math.round((total / workforce) * 100));
}

export function calcTrainingManHours(training: any[]): { totalManHours: number; byPeriod: { label: string; value: number }[] } {
  const byPeriod: Record<string, number> = {};
  let totalManHours = 0;
  safeArray(training).filter(i => i.Period).forEach(i => {
    const hours = ((parseFloat(i.NoOfAttendees) || 0) * (parseFloat(i.DurationMin) || 0)) / 60;
    totalManHours += hours;
    byPeriod[i.Period] = (byPeriod[i.Period] || 0) + hours;
  });
  const byPeriodArr = Object.entries(byPeriod)
    .sort(([a], [b]) => periodSortKey(a).localeCompare(periodSortKey(b)))
    .slice(-6)
    .map(([period, value]) => ({ label: periodLabel(period), value: Math.round(value * 10) / 10 }));
  return { totalManHours: Math.round(totalManHours * 10) / 10, byPeriod: byPeriodArr };
}

export function calcToolboxFrequency(training: any[]): { sessions: number; attendees: number } {
  const talks = safeArray(training).filter(
    i => /toolbox|tool\s*box|tbt/i.test(i.Category || '') || /toolbox|tool\s*box|tbt/i.test(i.TrainingSubject || ''),
  );
  return {
    sessions: talks.length,
    attendees: talks.reduce((s, i) => s + (parseFloat(i.NoOfAttendees) || 0), 0),
  };
}

export function calcToolboxByPeriod(training: any[]): { label: string; value: number }[] {
  const talks = safeArray(training).filter(
    i => /toolbox|tool\s*box|tbt/i.test(i.Category || '') || /toolbox|tool\s*box|tbt/i.test(i.TrainingSubject || ''),
  );
  const byPeriod: Record<string, number> = {};
  talks.filter(i => i.Period).forEach(i => {
    byPeriod[i.Period] = (byPeriod[i.Period] || 0) + 1;
  });
  return Object.entries(byPeriod)
    .sort(([a], [b]) => periodSortKey(a).localeCompare(periodSortKey(b)))
    .slice(-6)
    .map(([period, count]) => ({ label: periodLabel(period), value: count }));
}

// ─── Package Leaderboard ─────────────────────────────────────────────────────

export interface PackageRow {
  package: string;
  resolutionDays: number;
  wasteDiversion: number;
  nuisanceFreeDays: number;
  totalBreaches: number;
  trainingManHours: number;
  complianceScore: 'High' | 'Medium' | 'Low';
}

function deriveScore(breachPct: number, diversionRate: number): number {
  return (100 - breachPct) * 0.6 + diversionRate * 0.4;
}

export function calcPackageLeaderboard(
  allData: Record<string, any[]>,
  config: KpiDashboardConfig,
): PackageRow[] {
  const pkgSet = new Set<string>();
  Object.values(allData).forEach(arr =>
    safeArray(arr).forEach((i: any) => { if (i.packageName) pkgSet.add(i.packageName); }),
  );
  return Array.from(pkgSet)
    .sort()
    .map(pkg => {
      const f = (arr: any[]) => safeArray(arr).filter((i: any) => i.packageName === pkg);
      const breach = calcBreachRate(f(allData.noise), f(allData.vibration), f(allData.pm10), f(allData.pm25));
      const nuisance = calcNuisanceFreeDays(f(allData.noise), f(allData.vibration), f(allData.pm10), f(allData.pm25));
      const diversion = calcWasteDiversionRate(f(allData.waste));
      const manHours = calcTrainingManHours(f(allData.training));
      const score = deriveScore(breach.breachPercent, diversion);
      return {
        package: pkg,
        resolutionDays: calcAvgResolutionDays(f(allData.observations)),
        wasteDiversion: diversion,
        nuisanceFreeDays: nuisance.percent,
        totalBreaches: breach.totalBreachDays,
        trainingManHours: manHours.totalManHours,
        complianceScore: score >= 70 ? 'High' : score >= 40 ? 'Medium' : 'Low' as any,
      };
    });
}
